import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import { useEffect, useLayoutEffect, useRef } from 'react';

interface RovoGradientAnimationOptions {
  cellTarget?: 'field-cells' | 'streaming-markers';
}

/**
 * Encapsulates the animated Rovo border gradient for a field.
 * Sets CSS custom properties for the field boundaries and animates the gradient angle.
 * - Finds AIFC streaming markers or every cell in a specific field/column
 * - Dynamically updates dimensions when row heights change via ResizeObserver
 * - Computes bounding box coordinates for the selected field's cells only
 * - Sets CSS custom properties for positioning the gradient overlay
 * - Animates a CSS custom property `--database-rovo-gradient-angle` using requestAnimationFrame
 * - Handles both single and multi-cell selections with unified positioning
 * - For fields, constrains gradient between table header row and add entry button
 *
 * @param ref - Reference to the table container element
 * @param isEnabled - Whether the gradient animation is enabled
 * @param fieldId - The field/column ID to apply the gradient to
 * @param options - Chooses between AIFC streaming markers and every cell in the field
 */
export function useRovoGradientAnimationForCells(
  ref: React.RefObject<HTMLElement>,
  isEnabled: boolean,
  fieldId?: string,
  options: RovoGradientAnimationOptions = {}
) {
  const fieldIdRef = useRef(fieldId);
  fieldIdRef.current = fieldId;
  const cellTarget = options.cellTarget ?? 'streaming-markers';
  const cellTargetRef = useRef(cellTarget);
  cellTargetRef.current = cellTarget;
  const updateGradientDimensionsRef = useRef<(() => void) | undefined>();

  useEffect(() => {
    if (!isEnabled || !ref.current) {
      return;
    }

    const container = ref.current as HTMLElement;

    const updateGradientDimensions = () => {
      const currentFieldId = fieldIdRef.current;

      if (!currentFieldId) {
        return;
      }

      const fieldCells = container.querySelectorAll(
        cellTargetRef.current === 'field-cells'
          ? `td[data-field-id="${currentFieldId}"]`
          : `td[data-field-id="${currentFieldId}"] .${TABLE_CLASSNAMES.IS_ROVO_GRADIENT_SELECTED}`
      );
      const fieldHeader = container.querySelector(
        `th[data-field-id="${currentFieldId}"]`
      );

      const selectedCells: HTMLElement[] = [
        ...(fieldHeader ? [fieldHeader as HTMLElement] : []),
        ...(Array.from(fieldCells) as HTMLElement[])
      ];

      if (selectedCells.length === 0) {
        return;
      }

      // Calculate the bounding box that encompasses all selected cells
      const containerRect = container.getBoundingClientRect();

      const visibleCellRects = selectedCells
        .map((cell) => cell.getBoundingClientRect())
        .filter((rect) => {
          // Only check basic container bounds, not scroll viewport
          const isInHorizontalBounds =
            rect.right > containerRect.left && rect.left < containerRect.right;
          const isInVerticalBounds =
            rect.bottom > containerRect.top && rect.top < containerRect.bottom;

          return isInHorizontalBounds && isInVerticalBounds;
        });

      if (visibleCellRects.length === 0) {
        // No cells to show gradient for
        return;
      }

      let minLeft = visibleCellRects[0].left;
      let maxRight = visibleCellRects[0].right;
      let minTop = visibleCellRects[0].top;
      let maxBottom = visibleCellRects[0].bottom;

      for (let i = 1; i < visibleCellRects.length; i++) {
        const rect = visibleCellRects[i];
        minLeft = Math.min(minLeft, rect.left);
        maxRight = Math.max(maxRight, rect.right);
        minTop = Math.min(minTop, rect.top);
        maxBottom = Math.max(maxBottom, rect.bottom);
      }

      // Clip the gradient to the visible container viewport to prevent bleeding when scrolled
      const clippedLeft = Math.max(minLeft, containerRect.left);

      // Constrain the top to include the table header row, ignoring its padding-top
      let clippedTop: number;
      const headerRow = document.getElementById('table-layout-header-row');
      if (headerRow) {
        const headerRect = headerRow.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(headerRow);
        const paddingTop = parseFloat(computedStyle.paddingTop);
        // Start from the header content edge (after padding) and extend down to cells
        clippedTop = Math.max(minTop, headerRect.top + paddingTop);
      } else {
        clippedTop = Math.max(minTop, containerRect.top);
      }

      const clippedRight = Math.min(maxRight, containerRect.right);

      // Constrain the bottom to prevent gradient from extending over the add entry button
      let clippedBottom: number;

      // Find the add entry container by id and constrain gradient to not overlap it
      const addEntryContainer = document.getElementById('add-entry-quick-calc-container');

      if (addEntryContainer) {
        // Constrain gradient to not extend into the add entry container area
        const addEntryRect = addEntryContainer.getBoundingClientRect();
        clippedBottom = Math.min(maxBottom, addEntryRect.top);
      } else {
        clippedBottom = Math.min(maxBottom, containerRect.bottom);
      }

      const left = clippedLeft - containerRect.left;
      const top = clippedTop - containerRect.top;
      const width = clippedRight - clippedLeft;
      const height = clippedBottom - clippedTop;

      container.style.setProperty('--rovo-visibility', `visible`);
      container.style.setProperty('--rovo-cell-left', `${left}px`);
      container.style.setProperty('--rovo-cell-top', `${top}px`);
      container.style.setProperty('--rovo-cell-width', `${width}px`);
      container.style.setProperty('--rovo-cell-height', `${height}px`);
    };

    updateGradientDimensionsRef.current = updateGradientDimensions;
    updateGradientDimensions();

    const resizeObserver = new ResizeObserver(() => {
      updateGradientDimensions();
    });

    resizeObserver.observe(container);
    const mutationObserver =
      cellTargetRef.current === 'field-cells'
        ? new MutationObserver(updateGradientDimensions)
        : undefined;
    mutationObserver?.observe(container, { childList: true, subtree: true });

    let lastScrollTop = 0;

    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      const scrollTop = target.scrollTop;

      // Only update gradient on vertical scroll, not horizontal
      if (scrollTop !== lastScrollTop) {
        updateGradientDimensions();
      }

      lastScrollTop = scrollTop;
    };

    // Find the scrollable parent and add scroll listener
    let scrollableParent = container.parentElement;
    while (scrollableParent) {
      const computed = window.getComputedStyle(scrollableParent);
      if (computed.overflowY === 'auto' || computed.overflowY === 'scroll') {
        scrollableParent.addEventListener('scroll', handleScroll, { passive: true });
        break;
      }
      scrollableParent = scrollableParent.parentElement;
    }

    // Animate the gradient angle
    let frame = 0;
    let angle = 0;
    const animate = () => {
      angle = (angle + 1) % 360;
      container.style.setProperty('--database-rovo-gradient-angle', `${angle}deg`);
      frame = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (updateGradientDimensionsRef.current === updateGradientDimensions) {
        updateGradientDimensionsRef.current = undefined;
      }
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      mutationObserver?.disconnect();
      if (scrollableParent) {
        scrollableParent.removeEventListener('scroll', handleScroll);
      }
      container.style.setProperty('--rovo-visibility', `hidden`);
      container.style.removeProperty('--rovo-cell-left');
      container.style.removeProperty('--rovo-cell-top');
      container.style.removeProperty('--rovo-cell-width');
      container.style.removeProperty('--rovo-cell-height');
      container.style.removeProperty('--database-rovo-gradient-angle');
    };
    // Keep the field ID in a ref so moving the gradient does not restart its animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, ref]);

  useLayoutEffect(() => {
    if (isEnabled) {
      updateGradientDimensionsRef.current?.();
    }
  }, [cellTarget, fieldId, isEnabled]);
}

export default useRovoGradientAnimationForCells;
